type MimeType = "image/png" | "image/jpeg";

export function dataUrlToBase64(dataUrl: string, mimeType: MimeType = "image/png") {
  const prefix = `data:${mimeType};base64,`;
  return dataUrl.slice(prefix.length);
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(",");
  if (!header || !base64) throw new Error("Invalid data URL");

  const mime = /:(.*?);/.exec(header)?.[1] ?? "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

// Reads `items` rather than `files` because a screenshot in the OS buffer only lands there in some
// browsers. Omit `allowedTypes` to take any pasted file and let the caller validate.
export function filesFromClipboard(event: ClipboardEvent, allowedTypes?: string[]): File[] {
  return Array.from(event.clipboardData?.items ?? [])
    .filter((item) => item.kind === "file" && (!allowedTypes || allowedTypes.includes(item.type)))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
}

// Missing in older browsers and on pages not served over https.
export function canReadClipboard(): boolean {
  return "clipboard" in navigator && "read" in navigator.clipboard;
}

// Rejects when the browser refuses to read the clipboard.
export async function imageFromClipboard(allowedTypes: string[]): Promise<File | null> {
  for (const item of await navigator.clipboard.read()) {
    const type = item.types.find((itemType) => allowedTypes.includes(itemType));
    if (type) return new File([await item.getType(type)], "Pasted image", { type });
  }
  return null;
}

// True when a paste is a text field's own text paste. Word, Excel, and Outlook put a bitmap of the
// selection alongside the text, so a listener that does NOT own the field — a document- or
// container-scoped one — must leave these alone or it swallows the paste and files the phantom. A
// listener wired onto its own composer input owns the paste and should not consult this.
//
// Keyed on text/plain, not on any string item: "Copy image" in a browser carries text/html markup
// referencing the image, and that paste is a genuine upload.
export function pasteBelongsToTextField(event: ClipboardEvent): boolean {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  if (!target.closest("input, textarea, [contenteditable='true']")) return false;

  return Array.from(event.clipboardData?.items ?? []).some(
    (item) => item.kind === "string" && item.type === "text/plain",
  );
}
